<script setup>
// Auto-extracted chrome for HeizungZukunft.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Heizung Zukunft" },
    brandTagline: { type: String, default: "" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Heizung Zukunft");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800&display=swap";
</script>

<template>
    <div class="hzk" :style="`--hzk-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="hzk-topbar">
                    <div class="hzk-topbar__inner">
                        <a class="hzk-brand" href="/" aria-label="Startseite Heizung Zukunft">
                            <span class="hzk-brand__glyph" aria-hidden="true">
                                <svg viewBox="0 0 28 28" width="28" height="28">
                                    <path d="M14 2c2 4 6 6 6 11a6 6 0 1 1-12 0c0-5 4-7 6-11z" fill="currentColor"/>
                                </svg>
                            </span>
                            <span class="hzk-brand__txt">
                                <span class="hzk-brand__name">{{ brandName }}</span>
                                <span class="hzk-brand__sub">Community &middot; Magazin &middot; WP-Check</span>
                            </span>
                        </a>
                        <nav class="hzk-topbar__links" aria-label="Sekundärnavigation">
                            <a href="/ratgeber">Ratgeber</a>
                            <a href="#hzk-faq">Fragen</a>
                            <a href="#hzk-quiz" class="hzk-topbar__cta">Check starten</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="hzk-foot">
                    <div class="hzk-foot__inner">
                        <p class="hzk-foot__brand">{{ brandName }} &middot; {{ brandTagline }}</p>
                        <p v-if="author" class="hzk-foot__author">
                            Redaktionell verantwortlich: {{ author.name }}<span v-if="author.role"> &middot; {{ author.role }}</span>
                        </p>
                        <p class="hzk-foot__links">
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                            <a href="/kontakt">Kontakt</a>
                        </p>
                        <p class="hzk-foot__small">Stand 2026 - Förderkonditionen können sich jederzeit ändern.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.hzk {
    --hzk-ink: #0F172A;
    --hzk-ink-soft: #334155;
    --hzk-mute: #64748B;
    --hzk-paper: #FFFFFF;
    --hzk-cream: #F8FAFC;
    --hzk-hair: #E2E8F0;
    --hzk-hair-strong: #CBD5E1;
    --hzk-accent: #0891B2;
    color: var(--hzk-ink);
    background: var(--hzk-paper);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
}

/* ── Top bar ─────────────────────────────────────────────────────── */
.hzk-topbar {
    border-bottom: 1px solid var(--hzk-hair);
    background: var(--hzk-paper);
    position: sticky; top: 0; z-index: 30;
}
.hzk-topbar__inner {
    max-width: 1180px; margin: 0 auto;
    padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.hzk-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; color: inherit; }
.hzk-brand__glyph { color: var(--hzk-accent); display: inline-flex; }
.hzk-brand__name {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700; font-size: 17px; letter-spacing: -0.01em;
    display: block;
}
.hzk-brand__sub { display: block; font-size: 12px; color: var(--hzk-mute); letter-spacing: 0.02em; }
.hzk-topbar__links { display: flex; align-items: center; gap: 22px; }
.hzk-topbar__links a {
    font-size: 14px; color: var(--hzk-ink-soft); text-decoration: none; font-weight: 500;
}
.hzk-topbar__links a:hover { color: var(--hzk-ink); }
.hzk-topbar__cta {
    padding: 8px 14px; border: 1px solid var(--hzk-hair-strong); border-radius: 999px;
    color: var(--hzk-ink) !important;
}
.hzk-topbar__cta:hover { border-color: var(--hzk-accent); color: var(--hzk-accent) !important; }

/* ── Hero ────────────────────────────────────────────────────────── */
.hzk-hero {
    border-bottom: 1px solid var(--hzk-hair);
    background:
        radial-gradient(800px 320px at 80% -10%, rgba(8,145,178,0.08), transparent 70%),
        var(--hzk-paper);
}
.hzk-hero__inner {
    max-width: 980px; margin: 0 auto;
    padding: 72px 24px 56px;
    text-align: left;
}
.hzk-hero__eyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--hzk-ink-soft); font-weight: 600;
    margin: 0 0 18px;
}
.hzk-hero__pulse {
    width: 8px; height: 8px; border-radius: 50%; background: var(--hzk-accent);
    box-shadow: 0 0 0 4px rgba(8,145,178,0.18);
}
.hzk-hero__h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700; letter-spacing: -0.025em;
    font-size: clamp(38px, 6vw, 64px); line-height: 1.05;
    margin: 0 0 22px;
}
.hzk-hero__h1Accent {
    display: inline; color: var(--hzk-accent);
    border-bottom: 2px solid currentColor; padding-bottom: 2px;
}
.hzk-hero__lede {
    font-size: 18px; line-height: 1.55; color: var(--hzk-ink-soft);
    max-width: 640px; margin: 0 0 32px;
}
.hzk-hero__cta { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; margin-bottom: 40px; }
.hzk-hero__ctaMeta { font-size: 13px; color: var(--hzk-mute); }

.hzk-method {
    list-style: none; margin: 0; padding: 22px 0 0;
    border-top: 1px solid var(--hzk-hair);
    display: grid; gap: 18px;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.hzk-method__item {
    display: flex; flex-direction: column; gap: 4px;
}
.hzk-method__k {
    font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600;
}
.hzk-method__v { font-size: 14px; color: var(--hzk-ink); font-weight: 500; }

/* ── CTA buttons ─────────────────────────────────────────────────── */
.hzk-cta {
    appearance: none; border: 0; cursor: pointer;
    font-family: inherit; font-weight: 600; font-size: 15px;
    padding: 12px 22px; border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
    text-decoration: none; line-height: 1;
    transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}
.hzk-cta--primary {
    background: var(--hzk-accent); color: #fff;
    box-shadow: 0 1px 0 rgba(0,0,0,0.04);
}
.hzk-cta--primary:hover { transform: translateY(-1px); background: #0E7490; }
.hzk-cta--primary:disabled { background: #94A3B8; cursor: not-allowed; transform: none; }
.hzk-cta--ghost {
    background: transparent; color: var(--hzk-ink-soft);
    border: 1px solid var(--hzk-hair-strong);
}
.hzk-cta--ghost:hover { border-color: var(--hzk-ink); color: var(--hzk-ink); }
.hzk-cta--lg { padding: 16px 26px; font-size: 16px; }
.hzk-cta--full { width: 100%; padding: 16px 22px; }

/* ── Quiz section ────────────────────────────────────────────────── */
.hzk-quiz { padding: 64px 24px; background: var(--hzk-cream); }
.hzk-quiz__inner {
    max-width: 760px; margin: 0 auto;
    background: var(--hzk-paper);
    border: 1px solid var(--hzk-hair);
    border-radius: 14px;
    padding: 32px;
}
.hzk-quiz__head { margin-bottom: 28px; }
.hzk-quiz__chip {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600; margin: 0 0 12px;
    font-variant-numeric: tabular-nums;
}
.hzk-quiz__bar { height: 4px; background: var(--hzk-hair); border-radius: 999px; overflow: hidden; }
.hzk-quiz__barFill { display: block; height: 100%; transition: width 0.3s ease; }

.hzk-chapter__kicker {
    font-size: 13px; color: var(--hzk-mute); margin: 0 0 8px;
    text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
}
.hzk-chapter__title {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600; font-size: 26px; letter-spacing: -0.01em;
    margin: 0 0 24px;
}

/* ── Options ─────────────────────────────────────────────────────── */
.hzk-opts { display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px; }
.hzk-opt {
    appearance: none; cursor: pointer; text-align: left;
    background: var(--hzk-paper); color: var(--hzk-ink);
    border: 1px solid var(--hzk-hair-strong); border-radius: 10px;
    padding: 16px 18px;
    display: flex; align-items: flex-start; gap: 14px;
    font-family: inherit; font-size: 15px;
    transition: border-color 0.12s ease, background 0.12s ease;
}
.hzk-opt:hover { border-color: var(--hzk-accent); }
.hzk-opt--on {
    border-color: var(--hzk-accent);
    background: rgba(8,145,178,0.06);
    box-shadow: inset 0 0 0 1px var(--hzk-accent);
}
.hzk-opt__tick {
    width: 22px; height: 22px; border-radius: 50%;
    border: 1px solid var(--hzk-hair-strong);
    display: inline-flex; align-items: center; justify-content: center;
    color: transparent; flex-shrink: 0; margin-top: 2px;
}
.hzk-opt--on .hzk-opt__tick {
    background: var(--hzk-accent); border-color: var(--hzk-accent); color: #fff;
}
.hzk-opt__txt { display: flex; flex-direction: column; gap: 2px; }
.hzk-opt__main { font-weight: 600; font-size: 15px; }
.hzk-opt__hint { font-size: 13px; color: var(--hzk-mute); }

/* ── Slider ──────────────────────────────────────────────────────── */
.hzk-slider { margin-bottom: 28px; }
.hzk-slider__row { display: flex; align-items: center; gap: 16px; }
.hzk-slider__row input[type=range] {
    flex: 1; height: 4px; appearance: none; background: var(--hzk-hair); border-radius: 999px;
}
.hzk-slider__row input[type=range]::-webkit-slider-thumb {
    appearance: none; width: 22px; height: 22px; background: var(--hzk-accent);
    border-radius: 50%; border: 3px solid #fff;
    box-shadow: 0 0 0 1px var(--hzk-accent);
}
.hzk-slider__val {
    font-family: 'Space Grotesk', sans-serif; font-variant-numeric: tabular-nums;
    font-weight: 600; font-size: 18px; min-width: 90px; text-align: right;
}
.hzk-slider__scale {
    display: flex; justify-content: space-between; margin-top: 8px;
    font-size: 12px; color: var(--hzk-mute); font-variant-numeric: tabular-nums;
}

.hzk-chapter__nav {
    display: flex; gap: 12px; align-items: center; justify-content: flex-end;
    padding-top: 8px;
}

/* ── Result ──────────────────────────────────────────────────────── */
.hzk-result { display: flex; flex-direction: column; gap: 28px; }
.hzk-result__card {
    border: 1px solid var(--hzk-hair);
    border-left: 4px solid var(--hzk-stufe, var(--hzk-accent));
    border-radius: 10px;
    padding: 24px 26px;
    background: var(--hzk-paper);
}
.hzk-result__kicker {
    font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600; margin: 0 0 6px;
}
.hzk-result__h {
    font-family: 'Space Grotesk', sans-serif; font-weight: 700;
    font-size: 24px; letter-spacing: -0.01em; margin: 0 0 20px;
    color: var(--hzk-stufe, var(--hzk-ink));
}
.hzk-result__stats {
    list-style: none; padding: 0; margin: 0 0 18px;
    display: grid; gap: 12px;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}
.hzk-result__stats li {
    padding: 12px 14px; border: 1px solid var(--hzk-hair); border-radius: 8px;
    display: flex; flex-direction: column; gap: 4px;
}
.hzk-result__statK {
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600;
}
.hzk-result__statV {
    font-family: 'Space Grotesk', sans-serif; font-variant-numeric: tabular-nums;
    font-size: 22px; font-weight: 600;
}
.hzk-result__reasons {
    list-style: disc inside; padding: 0; margin: 0 0 14px;
    color: var(--hzk-ink-soft); font-size: 14px; line-height: 1.6;
}
.hzk-result__note { margin: 0; font-size: 12px; color: var(--hzk-mute); }

/* ── Lead form ───────────────────────────────────────────────────── */
.hzk-lead { display: flex; flex-direction: column; gap: 16px; }
.hzk-lead__h {
    font-family: 'Space Grotesk', sans-serif; font-weight: 600;
    font-size: 20px; margin: 0;
}
.hzk-lead__sub { margin: 0; font-size: 14px; color: var(--hzk-ink-soft); }
.hzk-lead__grid {
    display: grid; gap: 12px;
    grid-template-columns: 1fr 1fr;
}
.hzk-lead__lbl--full { grid-column: 1 / -1; }
.hzk-lead__lbl { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--hzk-ink-soft); font-weight: 500; }
.hzk-lead__lbl input {
    appearance: none; font: inherit;
    padding: 11px 13px; border: 1px solid var(--hzk-hair-strong);
    border-radius: 8px; background: #fff; color: var(--hzk-ink);
}
.hzk-lead__lbl input:focus { outline: none; border-color: var(--hzk-accent); box-shadow: 0 0 0 3px rgba(8,145,178,0.18); }
.hzk-lead__consent {
    display: flex; gap: 10px; align-items: flex-start;
    font-size: 12px; color: var(--hzk-ink-soft); line-height: 1.5;
    padding: 12px; background: var(--hzk-cream); border: 1px solid var(--hzk-hair); border-radius: 8px;
}
.hzk-lead__consent input { margin-top: 3px; }
.hzk-lead__err { color: #B91C1C; font-size: 12px; }
.hzk-lead__err--big { font-size: 13px; padding: 8px 12px; background: #FEF2F2; border-radius: 6px; }

.hzk-thanks {
    text-align: center; padding: 18px 8px;
    border: 1px solid var(--hzk-accent); border-radius: 12px;
    background: rgba(8,145,178,0.04);
}
.hzk-thanks__chip {
    display: inline-block; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
    background: var(--hzk-accent); color: #fff; padding: 4px 10px; border-radius: 999px; margin-bottom: 10px;
}
.hzk-thanks__h { font-family: 'Space Grotesk', sans-serif; font-size: 22px; margin: 0 0 8px; }
.hzk-thanks__sub { margin: 0; font-size: 14px; color: var(--hzk-ink-soft); }

/* ── Trust section ───────────────────────────────────────────────── */
.hzk-trust { padding: 72px 24px; border-top: 1px solid var(--hzk-hair); }
.hzk-trust__inner { max-width: 1080px; margin: 0 auto; }
.hzk-trust__kicker {
    font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600; margin: 0 0 10px;
}
.hzk-trust__h {
    font-family: 'Space Grotesk', sans-serif; font-weight: 700;
    font-size: clamp(28px, 4vw, 38px); letter-spacing: -0.02em;
    margin: 0 0 32px; max-width: 640px;
}
.hzk-trust__grid {
    display: grid; gap: 1px; background: var(--hzk-hair);
    border: 1px solid var(--hzk-hair); border-radius: 12px; overflow: hidden;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}
.hzk-trust__cell {
    background: var(--hzk-paper); padding: 28px 24px;
    display: flex; flex-direction: column; gap: 10px;
}
.hzk-trust__num {
    font-family: 'Space Grotesk', sans-serif; font-variant-numeric: tabular-nums;
    color: var(--hzk-accent); font-weight: 700; font-size: 14px; letter-spacing: 0.06em;
}
.hzk-trust__cell h3 { margin: 0; font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: 600; }
.hzk-trust__cell p { margin: 0; font-size: 14px; color: var(--hzk-ink-soft); line-height: 1.6; }

/* ── FAQ ─────────────────────────────────────────────────────────── */
.hzk-faq { padding: 72px 24px; background: var(--hzk-cream); border-top: 1px solid var(--hzk-hair); }
.hzk-faq__inner { max-width: 800px; margin: 0 auto; }
.hzk-faq__kicker {
    font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--hzk-mute); font-weight: 600; margin: 0 0 10px;
}
.hzk-faq__h {
    font-family: 'Space Grotesk', sans-serif; font-weight: 700;
    font-size: clamp(26px, 3.5vw, 34px); letter-spacing: -0.02em;
    margin: 0 0 28px;
}
.hzk-faq__list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.hzk-faq__item details {
    background: var(--hzk-paper); border: 1px solid var(--hzk-hair); border-radius: 10px;
    padding: 18px 20px;
}
.hzk-faq__item summary {
    cursor: pointer; font-family: 'Space Grotesk', sans-serif;
    font-weight: 600; font-size: 16px; list-style: none;
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
}
.hzk-faq__item summary::after {
    content: '+'; font-weight: 400; font-size: 22px; color: var(--hzk-accent); transition: transform 0.2s ease;
}
.hzk-faq__item details[open] summary::after { transform: rotate(45deg); }
.hzk-faq__item details p { margin: 12px 0 0; font-size: 14px; color: var(--hzk-ink-soft); line-height: 1.6; }

/* ── Footer ──────────────────────────────────────────────────────── */
.hzk-foot { border-top: 1px solid var(--hzk-hair); padding: 32px 24px; background: var(--hzk-paper); }
.hzk-foot__inner {
    max-width: 1080px; margin: 0 auto;
    display: flex; flex-direction: column; gap: 8px;
    font-size: 13px; color: var(--hzk-mute);
}
.hzk-foot__brand { font-family: 'Space Grotesk', sans-serif; font-weight: 600; color: var(--hzk-ink); margin: 0; }
.hzk-foot__author { margin: 0; }
.hzk-foot__links { margin: 0; display: flex; gap: 18px; flex-wrap: wrap; }
.hzk-foot__links a { color: var(--hzk-ink-soft); text-decoration: none; }
.hzk-foot__links a:hover { color: var(--hzk-accent); }
.hzk-foot__small { margin: 0; font-size: 12px; }

/* ── Sticky mobile CTA ───────────────────────────────────────────── */
.hzk-sticky {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: var(--hzk-paper);
    border-top: 1px solid var(--hzk-hair);
    box-shadow: 0 -4px 16px rgba(15,23,42,0.06);
}
@media (min-width: 768px) {
    .hzk-sticky { display: none; }
}

/* ── Responsive ──────────────────────────────────────────────────── */
@media (max-width: 640px) {
    .hzk-topbar__links a:not(.hzk-topbar__cta) { display: none; }
    .hzk-hero__inner { padding: 48px 20px 40px; }
    .hzk-quiz { padding: 32px 16px; }
    .hzk-quiz__inner { padding: 22px; }
    .hzk-lead__grid { grid-template-columns: 1fr; }
    .hzk-chapter__nav { justify-content: space-between; }
}

/* ── Trust strip (images + cites + byline) — hzk-* unique treatment ── */
.hzk-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.hzk-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .hzk-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.hzk-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 2px double #0891B2; box-shadow: 0 6px 22px -14px rgba(20,17,13,0.22);
}
.hzk-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.hzk-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.hzk-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.hzk-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.hzk-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.hzk-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #0891B255;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.hzk-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hzk-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #0891B2;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.hzk-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.hzk-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.hzk-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.hzk-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.hzk-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.hzk-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
